/* eslint-disable consistent-return */
import * as React from "react";
import { Icon, VideoPlayer, Button } from "ui/components";
import { Channel } from "core/models";
import { useLivestreamContext } from "ui/context/LivestreamContext";
import getChannelURL from "utils/getChannelURL";
import styles from "./LivestreamPlayer.module.scss";

interface Props {
    channel: Channel;
}

type Point = {
    x: number;
    y: number;
};

const LivestreamPlayer: React.FC<Props> = ({ channel }) => {
    const { removeChannel } = useLivestreamContext();
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const [urlReachability, setURLReachability] = React.useState<
        boolean | null
    >(null);
    const [initialPosition, setInitialPosition] = React.useState<Point | null>(
        null,
    );
    const [initialMouse, setInitialMouse] = React.useState<Point | null>(null);
    const loc = React.useRef<Point | null>(null);
    const [, setLocation] = React.useState<Point | null>(null);
    const [dragging, setDragging] = React.useState<boolean>(false);
    const [, setLoadingVideo] = React.useState<boolean>(true);

    const connectionTest = async () => {
        try {
            const result = await fetch(getChannelURL(channel.key));
            setURLReachability(result.status === 200);
            if (result.status !== 200) {
                setLoadingVideo(false);
            }
        } catch (err) {
            setURLReachability(false);
        }
    };

    const mouseMoved = (event: MouseEvent) => {
        if (!dragging) {
            return;
        }

        const { clientX, clientY } = event;
        const nextX =
            clientX -
            (initialMouse?.x || 0) +
            (initialPosition ? initialPosition.x : 0);
        const nextY =
            clientY -
            (initialMouse?.y || 0) +
            (initialPosition ? initialPosition.y : 0);
        loc.current = { x: nextX, y: nextY };
        setLocation({ x: nextX, y: nextY });
    };
    const mouseUp = () => {
        const container = containerRef.current;
        if (!container) {
            return;
        }
        setDragging(false);
    };
    const mouseDown = (event: MouseEvent) => {
        const container = containerRef.current;
        if (!container) {
            return;
        }
        const { clientX, clientY } = event;
        const newLocation = loc.current ? { ...loc.current } : null;
        setInitialPosition(newLocation);
        setInitialMouse({
            x: clientX,
            y: clientY,
        });
        setDragging(true);
    };

    const cleanup = () => {
        window.removeEventListener("mouseup", mouseUp);
        window.removeEventListener("mousemove", mouseMoved);
    };

    React.useEffect(() => {
        const container = containerRef.current;
        if (!container) {
            return;
        }

        window.addEventListener("mousemove", mouseMoved);
        window.addEventListener("mouseup", mouseUp);
        return () => cleanup();
    }, [initialPosition, initialMouse, loc.current, dragging]);

    React.useEffect(() => {
        containerRef.current?.addEventListener("mousedown", mouseDown);
        connectionTest();
        return () =>
            containerRef.current?.removeEventListener("mousedown", mouseDown);
    }, []);

    const offsets = loc.current
        ? {
              transform: `translate(${loc.current.x}px, ${loc.current.y}px)`,
          }
        : {};
    const containerStyles = {
        ...offsets,
        cursor: dragging ? "grabbing" : "grab",
    };

    const close = () => {
        removeChannel(channel);
    };

    // const popoutStream = () => {
    //     const link = document.createElement("a");
    //     link.target = "_blank";
    //     link.href = `/livestream/${channel.key}`;
    //     document.body.appendChild(link);
    //     link.click();
    //     document.body.removeChild(link);
    //     close();
    // };

    const renderPlaceholders = () => (
            <>
                {urlReachability === false && (
                    <div className={styles.notice}>
                        <Icon name="error" />
                        <br />
                        <span>Stream unavailable</span>
                    </div>
                )}
                {!channel && (
                    <div className={styles.notice}>
                        <Icon name="error" />
                        <br />
                        <span>No stream selected</span>
                    </div>
                )}
            </>
        );

    return (
        <div
            className={styles.container}
            ref={containerRef}
            style={containerStyles}
        >
            <Button
                type="function"
                theme="light"
                icon="close"
                className={styles.closebutton}
                onClick={close}
                float
            />
            {urlReachability && (
                <div className={styles.player__container}>
                    <VideoPlayer
                        type="stream"
                        videoSrc={getChannelURL(channel.exStream.name)}
                        useClickOverlay={false}
                        autoplay
                    />
                </div>
            )}
            {renderPlaceholders()}
        </div>
    );
};

export default LivestreamPlayer;
